import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button, Input, toast } from '@nokowazzi/ui';
import { AuthScreen } from '../features/auth/AuthScreen';
import { signInSchema, translateFirstError } from '../features/auth/forms';
import { supabase } from '../lib/supabase';

/** 로그인 성공 후 이동할 내부 경로 — open redirect 방지로 '/' 시작만 허용 */
const signInSearchSchema = z.object({
  redirect: z.string().startsWith('/').optional(),
});

export const Route = createFileRoute('/auth/signin')({
  validateSearch: (search): z.infer<typeof signInSearchSchema> => {
    const parsed = signInSearchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: SignInScreen,
});

interface FieldErrors {
  email?: string;
  password?: string;
}

function SignInScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { redirect } = Route.useSearch();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const parsed = signInSchema.safeParse({ email, password });
    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      setErrors({
        email: translateFirstError(t, fieldErrors.email),
        password: translateFirstError(t, fieldErrors.password),
      });
      return;
    }

    setErrors({});
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: parsed.data.email,
      password: parsed.data.password,
    });
    setSubmitting(false);

    if (error) {
      toast.show({ type: 'error', message: t('auth.signInFailed') });
      return;
    }

    // redirect는 임의 내부 경로라 typed navigate 대신 history로 이동
    router.history.replace(redirect ?? '/');
  };

  return (
    <AuthScreen title={t('auth.signInTitle')}>
      <form noValidate className="flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)}>
        <Input
          type="email"
          label={t('auth.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
        />
        <Input
          type="password"
          label={t('auth.password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          autoComplete="current-password"
        />
        <Button type="submit" size="lg" fullWidth loading={submitting} className="mt-2">
          {t('actions.signIn')}
        </Button>
      </form>
      <p className="mt-6 text-center text-caption1 text-fg-secondary">
        {t('auth.noAccount')}{' '}
        <Link
          to="/auth/signup"
          className="font-semibold text-fg underline underline-offset-2"
        >
          {t('actions.signUp')}
        </Link>
      </p>
    </AuthScreen>
  );
}
